/** @format */

import { createApi, fetchBaseQuery as originalFetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { ARR_TAGS, PROVIDES_AND_INVALIDATE_TAGS } from '../../constants'
import { transformDataResponse } from '../../utils'
import { encrypt } from '../Encriptaciones'
const TWO_MINUTES = 120
const UnAuthorized = 401
const baseQuery = originalFetchBaseQuery({
  baseUrl: process.env.REACT_APP_RUTA,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('token')
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  }
})

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === UnAuthorized) {
    encrypt.clearSession()
    window.location.href = '/login'
    return
  }

  return result
}
// Define a service using a base URL and expected endpoints
export const clinicalApi = createApi({
  reducerPath: 'clinicalApi',
  tagTypes: ARR_TAGS,
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (body) => ({
        url: '/auth',
        body,
        method: 'POST'
      })
    }),
    getPeoples: builder.query({
      query: (filtering = '') => {
        const filter = filtering.length ? { filter: filtering } : {}
        return {
          url: '/personas',
          params: filter
        }
      },
      transformResponse: (response) =>
        response.data?.map((item) => ({
          ...item,
          fullName: `${item.primerNombre} ${item.primerApellido}`
        })),

      keepUnusedDataFor: TWO_MINUTES,
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PEOPLE]
    }),

    getAllergics: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => ({
        url: `/alergias`,
        params: {
          limit: 200
        }
      }),
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),
    allergicById: builder.query({
      query: (id) => `/alergias/${id}`,
      keepUnusedDataFor: 10,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),

    createAllergics: builder.mutation({
      query: (body) => ({
        url: '/alergias',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),
    updateAllergics: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/alergias/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),
    deleteAllergics: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/alergias/status/${id}`,
        method: 'PATCH',
        body: { activo: !activo }
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),

    getSymptomsOfAllergies: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/sintomas-alergias`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIC_SYMPTOMS]
    }),

    getModules: builder.query({
      query: () => ({
        url: '/modulo',
        params: {}
      }),

      keepUnusedDataFor: TWO_MINUTES
    }),
    getUsers: builder.query({
      query: (filter = '') => {
        const params = filter.length ? { search: filter } : {}
        return {
          url: '/user',
          params,
          keepUnusedDataFor: TWO_MINUTES
        }
      }
    }),

    //* REGISTROS
    getHereditaryDiseases: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/registros/enfermedad-hereditaria?limit=100`
    }),
    getPathologicalDiseases: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/registros/patologica-caracteristica`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    getRelationShipToDiseases: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/registros/comodin/parentesco`
    }),
    getEts: builder.query({
      query: () => '/registros/comodin/vaginitis',
      keepUnusedDataFor: TWO_MINUTES
    }),
    getSmookingData: builder.query({
      query: () => '/registros/comodin/tabaquismo',
      keepUnusedDataFor: TWO_MINUTES
    }),
    getAlcholismData: builder.query({
      query: () => '/registros/comodin/alcoholismo',
      keepUnusedDataFor: TWO_MINUTES
    }),

    // * GENERALES
    getPerinatalData: builder.query({
      query: () => '/generales/puntuaciones',

      keepUnusedDataFor: TWO_MINUTES
    }),
    getForContraceptiveMethods: builder.query({
      query: () => ({
        url: '/generales/alteracion-menstrual',
        params: {
          limit: 200
        }
      }),
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.MENSTRUATION]
    }),
    getAlteracionMenstrual: builder.query({
      query: () => ({
        url: '/generales/alteracion-menstrual',
        params: {
          limit: 200
        }
      }),
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.MENSTRUATION]
    }),

    getContraceptiveMethods: builder.query({
      query: () => '/generales/anticonceptivos',
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ANTICONCEPTION]
    }),

    anticonceptivos: builder.query({
      query: () => ({
        url: '/anticoncepcion',
        params: {
          limit: 200
        }
      }),
      transformResponse: (response) => response.data,
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ANTICONCEPTION]
    }),

    getRelationships: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/generales/parentesco`
    }),
    getIdentityCard: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/generales/documentos-identidad`
    }),
    getProfessions: builder.query({
      keepUnusedDataFor: TWO_MINUTES,
      query: () => `/generales/profesion`
    }),
    getSexuallyTransmittedDiseases: builder.query({
      query: () => '/generales/ets',

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ETS]
    }),

    getPregnancyHistory: builder.query({
      query: () => '/generales/antecedente-embarazo',

      keepUnusedDataFor: TWO_MINUTES
    }),
    getObstetricDiseases: builder.query({
      query: () => '/generales/enfermedad-obstetrica',

      keepUnusedDataFor: TWO_MINUTES
    }),
    getDrugs: builder.query({
      query: () => '/generales/toxinas',

      keepUnusedDataFor: TWO_MINUTES
    }),
    severities: builder.query({
      query: () => '/generales/severidad',
      keepUnusedDataFor: TWO_MINUTES
    }),

    //* EXPEDIENTES
    updateIncapacity: builder.mutation({
      query: ({ id, ...rest }) => {
        return {
          url: `/expedientes/incapacidad/${id}`,
          method: 'PATCH',
          body: rest
        }
      },
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.INCAPACITIES]
    }),
    changeDoctor: builder.mutation({
      query: ({ id, doctorId }) => ({
        url: `expedientes/changes/medico/${id}`,
        method: 'PATCH',
        body: { medicoId: doctorId }
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.CLINICAL_RECORDS]
    }),

    deleteIncapcity: builder.mutation({
      query: (id) => ({
        url: `/expedientes/incapacidad/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.INCAPACITIES]
    }),

    getIncapacities: builder.query({
      query: ({ id }) => {
        if (!id) return `/expedientes/incapacidad/0`

        return `/expedientes/incapacidad/${id}`
      },
      keepUnusedDataFor: TWO_MINUTES,
      transformResponse: (response) =>
        response?.map((item) => {
          const { fechaInicio, fechaFin, ...rest } = item

          return {
            ...rest,
            fechaInicio: fechaInicio?.split('T')?.[0],
            fechaFin: fechaFin?.split('T')?.[0]
          }
        }),
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.INCAPACITIES]
    }),
    getNoPathologicalDiseaseForFile: builder.query({
      query: (id) => `/expedientes/no-patologico/${id}`,

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.NO_PATHOLOGICAL_DISEASES]
    }),
    getPregnancyHistoryForFile: builder.query({
      query: (id) => `/expedientes/perenitales/${id}`,

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.PERINATAL_DATA]
    }),
    getInterrogationForFile: builder.query({
      query: (id) => `/expedientes/consultas/${id}`,
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),
    getFolderById: builder.query({
      query: (id) => `/expedientes/${id}`,

      keepUnusedDataFor: TWO_MINUTES,
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.CLINICAL_RECORDS]
    }),
    getFolderPeoples: builder.query({
      query: (search = '') => {
        const params = search ? { search } : {}
        return {
          url: '/expedientes/pacientes',
          params: {
            ...params,
            limit: 1_000
          }
        }
      },
      transformResponse: (response) => response.data?.map((item) => item),
      invalidatesTags: [
        PROVIDES_AND_INVALIDATE_TAGS.CLINICAL_RECORDS,
        PROVIDES_AND_INVALIDATE_TAGS.PEOPLE
      ],

      keepUnusedDataFor: 30
    }),
    getClinicalRecords: builder.query({
      query: (filter = '') => {
        const search = filter.length ? { search: filter } : {}
        return {
          url: '/expedientes',
          params: search
        }
      },
      transformResponse: (response) => response.data?.map((item) => item),

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CLINICAL_RECORDS]
    }),

    // * MUTACIONES EXPEDIENTES
    saveIncapacity: builder.mutation({
      query: (body) => ({
        url: '/expedientes/incapacidad',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.INCAPACITIES]
    }),
    saveAllergies: builder.mutation({
      query: (body) => ({
        url: '/expedientes/alergias',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),
    saveHereditary: builder.mutation({
      query: (body) => ({
        url: '/expedientes/hereditario',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.HEREDITARIES]
    }),
    savePathologicalDiseases: builder.mutation({
      query: (body) => ({
        url: '/expedientes/patologico',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    saveNoPathologicalDiseases: builder.mutation({
      query: (body) => ({
        url: '/expedientes/no-patologico',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.NO_PATHOLOGICAL_DISEASES]
    }),
    savePerinatalData: builder.mutation({
      query: (body) => ({
        url: '/expedientes/perenitales',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PERINATAL_DATA]
    }),
    saveFisicalExploration: builder.mutation({
      query: (body) => ({
        url: '/expedientes/exploracion-fisica',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.FISICAL_EXPLORATION]
    }),

    saveExpedient: builder.mutation({
      query: (body) => ({
        url: '/expedientes',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.CLINICAL_RECORDS]
    }),
    getFisicalExplorationForFile: builder.query({
      query: (id) => `/expedientes/exploracion-fisica/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.FISICAL_EXPLORATION]
    }),
    getAllergiesForFile: builder.query({
      query: (id) => `/expedientes/alergias/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIES]
    }),

    saveGinecological: builder.mutation({
      query: (body) => ({
        url: '/expedientes/ginecologico',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.GINECOLOGICAL]
    }),
    saveObstetric: builder.mutation({
      query: (body) => ({
        url: '/expedientes/obstretico',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.OBSTETRIC]
    }),
    getObstetricDiseasesForFile: builder.query({
      query: (id) => `/expedientes/obstretico/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.OBSTETRIC]
    }),
    getGinecologicalForFile: builder.query({
      query: (id) => `/expedientes/ginecologico/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.GINECOLOGICAL]
    }),
    getHereditariesForFile: builder.query({
      query: (id) => `/expedientes/hereditario/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.HEREDITARIES]
    }),
    getPathologicalDiseasesForFile: builder.query({
      query: (id) => `/expedientes/patologico/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    // ? DIAGNOSTICOS
    getDiagnostics: builder.query({
      query: (id) => '/consultas/diagnostico'
    }),

    // ? PRESCRIPCIONES O CONSULTAS
    saveInterrogation: builder.mutation({
      query: (body) => ({
        url: '/consultas',
        method: 'POST',
        body
      }),
      invalidatesTags: [
        PROVIDES_AND_INVALIDATE_TAGS.INTERROGATION,
        PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS
      ]
    }),

    updateConsulta: builder.mutation({
      query: ({ body, consultaId }) => {
        return {
          url: `/consultas/${consultaId}`,
          method: 'PATCH',
          body
        }
      },
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),

    deleteConsulta: builder.mutation({
      query: ({ id }) => ({
        url: `/consultas/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),
    createDiagnostico: builder.mutation({
      query: (body) => ({
        url: `/consultas/consulta-diagnostico`,
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.DIAGNOSTICOS]
    }),
    updateDiagnostico: builder.mutation({
      query: ({ body, id }) => ({
        url: `/consultas/consulta-diagnostico/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.DIAGNOSTICOS]
    }),
    deleteDiagnostico: builder.mutation({
      query: ({ id }) => ({
        url: `/consultas/consulta-diagnostico/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.DIAGNOSTICOS]
    }),

    diagnosticos: builder.query({
      query: (filter = '') => {
        const params = filter.length > 0 ? { search: filter } : {}
        return {
          url: '/consultas/diagnostico',
          params
        }
      },
      transformResponse: (response) => response.data?.map((item) => item),

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.DIAGNOSTICOS]
    }),
    consultas: builder.query({
      query: ({ search = '' }) => {
        const params = search?.length ? { search } : {}
        return {
          url: '/consultas',
          params: {
            ...params,
            limit: 1_000
          },
          method: 'GET'
        }
      },
      transformResponse: (response) => response.data?.map((item) => item),
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS],

      keepUnusedDataFor: TWO_MINUTES
    }),
    searchConsultaById: builder.query({
      query: (consultaId) => {
        return {
          url: `/consultas/${consultaId}`,
          method: 'GET'
        }
      },
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS],

      keepUnusedDataFor: TWO_MINUTES
    }),
    consultaDiagnosticos: builder.query({
      query: (consultaId) => `/consultas/consulta-diagnostico/diagnostico/${consultaId}`,
      transformResponse: (response) => response?.data?.map((item) => item) ?? response,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.DIAGNOSTICOS],
      keepUnusedDataFor: TWO_MINUTES
    }),
    consultaDiagnostico: builder.query({
      query: (diagnosticoId) => `/consultas/consulta-diagnostico/${diagnosticoId}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),
    consultaById: builder.query({
      query: (id) => `/consultas/${id}`,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),

    consultasPorExpediente: builder.query({
      query: (id) => `/expedientes/consultas/${id}`,

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),

    //  GENERALES
    genero: builder.query({
      query: () => `/generales/sexo`,

      keepUnusedDataFor: TWO_MINUTES
    }),
    paises: builder.query({
      query: () => '/generales/paises',

      keepUnusedDataFor: TWO_MINUTES
    }),
    departamentos: builder.query({
      query: (paisId = 61) => ({
        url: '/generales/departamentos',
        params: { paisId }
      })
    }),
    municipio: builder.query({
      query: (departamentoId) => ({
        url: `/generales/ciudades`,
        params: {
          departamentoId
        }
      })
    }),

    // MEDICOS CRUD
    crearMedico: builder.mutation({
      query: (body) => ({
        url: `/medico`,
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.MEDICOS]
    }),
    updateMedico: builder.mutation({
      query: ({ medicoId, ...body }) => {
        return {
          url: `/medico/${medicoId}`,
          method: 'PATCH',
          body
        }
      },
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.MEDICOS]
    }),
    deleteMedico: builder.mutation({
      query: (id) => ({
        url: `/medico/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.MEDICOS]
    }),
    updateMedicoByConsulta: builder.mutation({
      query: ({ medicoId, consultaId }) => ({
        url: `/consultas/${consultaId}/medicos`,
        method: 'PATCH',
        body: { medicoId }
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.CONSULTAS]
    }),

    medicos: builder.query({
      query: (filter = null) => {
        const params = filter ? { search: filter } : {}
        return {
          url: `/medico`,
          params: {
            ...params,
            limit: 1_000
          }
        }
      },
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.MEDICOS],
      transformResponse: (response) => response.data?.map((item) => item)
    }),
    doctorById: builder.query({
      query: (id) => `/medico/${id}`,
      keepUnusedDataFor: 60 //? ONE MINUTE
    }),

    // ? ESPECIALIDADES
    especialidades: builder.query({
      query: () => ({
        url: `/especialidades`,
        params: {
          limit: 1_000
        }
      }),
      transformResponse: (response) => response.data?.map((item) => item),
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ESPECIALIDADES]
    }),
    subEspecialidades: builder.query({
      query: () => ({
        url: `/sub-especialidades`,
        params: {
          limit: 1_000
        }
      }),
      transformResponse: (response) => response.data?.map((item) => item),
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.SUB_ESPECIALIDAD]
    }),
    crearEspecialidad: builder.mutation({
      query: (body) => ({
        url: `/especialidades`,
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ESPECIALIDADES]
    }),
    updateEspecialidad: builder.mutation({
      query: ({ id, ...body }) => {
        return {
          url: `/especialidades/${id}`,
          method: 'PATCH',
          body
        }
      },
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ESPECIALIDADES]
    }),
    crearSubEspecialidad: builder.mutation({
      query: (body) => ({
        url: `/sub-especialidades`,
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.SUB_ESPECIALIDAD]
    }),
    updateSubEspecialidad: builder.mutation({
      query: ({ id, ...body }) => {
        return {
          url: `/sub-especialidades/${id}`,
          method: 'PATCH',
          body
        }
      },
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.SUB_ESPECIALIDAD]
    }),
    deleteSubEspecialidad: builder.mutation({
      query: (id) => ({
        url: `/sub-especialidades/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.SUB_ESPECIALIDAD]
    }),

    deleteEspecialidad: builder.mutation({
      query: (id) => ({
        url: `/especialidades/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ESPECIALIDADES]
    }),
    // ?   agenda de citas
    dates: builder.query({
      query: ({ medicoId = null }) => {
        const params = medicoId ? { medicoId } : {}
        return {
          url: '/citas',
          params
        }
      },

      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.DATES]
    }),
    newDate: builder.mutation({
      query: (body) => ({
        url: '/citas',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.DATES]
    }),
    updateDate: builder.mutation({
      query: ({ dateId, body }) => ({
        url: `/citas/${dateId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.DATES]
    }),
    statusDates: builder.query({
      query: (search = '') => {
        const params = search ? { search } : {}
        return {
          url: '/citas/estados',
          params: {
            ...params,
            limit: 1_000
          }
        }
      },
      transformResponse: (response) => response.data,

      keepUnusedDataFor: TWO_MINUTES
    }),
    // ?   SUCURSALES
    getSucursales: builder.query({
      query: ({ search = '' }) => {
        const params = search ? { search } : {}
        return {
          url: '/sucursales',
          params: { limit: 200, ...params }
        }
      },
      transformResponse: (response) => response.data,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.SUCURSALES]
    }),
    deleteLocale: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/sucursales/status/${id}`,
        method: 'PATCH',
        body: {
          activo: !activo
        }
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.SUCURSALES]
    }),

    // ? ENFERMEDADES HEREDITARIAS CRUD
    hereditaries: builder.query({
      query: (search = '') => {
        const params = search ? { search } : {}
        return {
          url: '/hereditario',
          params: {
            ...params,
            limit: 1_000
          }
        }
      },
      transformResponse: (response) => response,
      keepUnusedDataFor: TWO_MINUTES, //? 2 minutos

      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.HEREDITARIES]
    }),

    hereditaryById: builder.query({
      query: (id) => `/hereditario/${id}`,
      keepUnusedDataFor: 10
    }),
    // Mutations
    saveHereditaryDisease: builder.mutation({
      query: (body) => ({
        url: '/hereditario',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.HEREDITARIES]
    }),
    updateHereditaryDisease: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/hereditario/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.HEREDITARIES]
    }),

    deleteHereditaryDisease: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/hereditario/status/${id}`,
        method: 'PATCH',
        body: { activo: !activo }
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.HEREDITARIES]
    }),

    // ? complicaciones CRUD
    addComplication: builder.mutation({
      query: (body) => ({
        url: '/complicaciones',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.COMPLICATIONS]
    }),
    updateComplication: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/complicaciones/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.COMPLICATIONS]
    }),
    deleteComplication: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/complicaciones/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.COMPLICATIONS]
    }),
    getComplications: builder.query({
      query: () => ({ url: '/complicaciones', params: { limit: 200 } }),
      transformResponse: (response) => response.data.map((item) => item),
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.COMPLICATIONS]
    }),

    // ? menstruacion CRUD
    addMenstruation: builder.mutation({
      query: (body) => ({
        url: '/menstruacion',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.MENSTRUATION]
    }),
    getMenstruacionById: builder.query({
      query: (id) => `/menstruacion/${id}`,
      keepUnusedDataFor: 10
    }),

    updateMenstruation: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/menstruacion/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.MENSTRUATION]
    }),
    getComplicationById: builder.query({
      query: (id) => `/complicaciones/${id}`,
      keepUnusedDataFor: 10
    }),
    deleteMenstruation: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/menstruacion/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.MENSTRUATION]
    }),

    // ? ETS CRUD
    addEts: builder.mutation({
      query: (body) => ({
        url: '/ets',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ETS]
    }),
    getEnfermedadesTransmicionSexual: builder.query({
      query: () => ({
        url: '/ets',
        params: {
          limit: 200
        }
      }),
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ETS],
      transformResponse: (response) => response.data.map((item) => item)
    }),

    getEtsById: builder.query({
      query: (id) => `/ets/${id}`,
      keepUnusedDataFor: 10
    }),
    updateEts: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/ets/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ETS]
    }),
    deleteEts: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/ets/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ETS]
    }),

    // ? ANTICONCEPCION CRUD
    getAnticoncepcionById: builder.query({
      query: (id) => `/anticoncepcion/${id}`,
      keepUnusedDataFor: 10
    }),

    addAnticoncepcion: builder.mutation({
      query: (body) => ({
        url: '/anticoncepcion',
        method: 'POST',
        body
      }),

      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ANTICONCEPTION]
    }),
    updateAnticoncepcion: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/anticoncepcion/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ANTICONCEPTION]
    }),
    deleteAnticoncepcion: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/anticoncepcion/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ANTICONCEPTION]
    }),

    // ? sintomas de alergias
    addAllergicSymptom: builder.mutation({
      query: (body) => ({
        url: '/sintomas-alergias',
        method: 'POST',
        body: {
          ...body,
          descripcion: ''
        }
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIC_SYMPTOMS]
    }),
    updateAllergicSymptom: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sintomas-alergias/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIC_SYMPTOMS]
    }),
    deleteAllergicSymptom: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/sintomas-alergias/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ALLERGIC_SYMPTOMS]
    }),
    getAllergicSymptomsById: builder.query({
      query: (id) => `/sintomas-alergias/${id}`,
      keepUnusedDataFor: 10
    }),

    // ? PATOLOGIAS
    addPathologicalDisease: builder.mutation({
      query: (body) => ({
        url: '/registros/enfermedad-patologica',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    updatePathologicalDisease: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/registros/enfermedades-patologicas/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    deletePathologicalDisease: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/registros/enfermedad-patologica/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    findPathologicalDiseaseById: builder.query({
      query: (id) => `/registros/enfermedad-patologica/${id}`,
      keepUnusedDataFor: 10
    }),

    // ? ENFERMEDADES PATOLOGICAS
    addPathologicalDiseaseFeature: builder.mutation({
      query: (body) => ({
        url: '/registros/enfermedad-patologica-caracteristica',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    updatePathologicalDiseaseFeature: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/registros/enfermedad-patologica-caracteristica/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    deletePathologicalDiseaseFeature: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/registros/enfermedad-patologica-caracteristica/status/${id}`,
        body: { activo: !activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),
    findPathologicalDiseaseFeatureById: builder.query({
      query: (id) => `/registros/enfermedad-patologica-caracteristica/${id}`,
      keepUnusedDataFor: 10
    }),
    getPathologicalDiseasesFeatures: builder.query({
      query: () => '/registros/enfermedad-patologica-caracteristica',
      keepUnusedDataFor: TWO_MINUTES,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.PATHOLOGICAL_DISEASES]
    }),

    // ? send report
    sendReport: builder.mutation({
      query: ({ doctorId, ...body }) => ({
        url: `/medico/reportes-medicos/${doctorId}`,
        method: 'PATCH',
        body,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }),

    // ? ROLES
    getRoles: builder.query({
      query: (filter = '') => ({
        url: '/roles',
        params: { filter },
        keepUnusedDataFor: TWO_MINUTES
      }),
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ROLES]
    }),
    roleById: builder.query({
      query: (id) => `/roles/${id}`,
      keepUnusedDataFor: 10,
      transformResponse: (response) => {
        return {
          roles: response.roles,
          modulos: response.modulos.map(({ id_modulo, ...rest }) => ({
            ...rest,
            moduleId: id_modulo
          }))
        }
      },
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.ROLES]
    }),
    permissionsByRoleId: builder.query({
      query: (rolId) => `/permisos/roles/${rolId}`,
      keepUnusedDataFor: 10,
      transformResponse: (response) => transformDataResponse(response)
    }),
    asignPermissionsToRole: builder.mutation({
      query: (body) => ({
        url: '/permisos',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ROLES]
    }),

    asignPermissionUpdate: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/permisos/roles/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.ROLES]
    }),
    // ? EMPRESAS

    empresas: builder.query({
      query: () => '/empresas',
      keepUnusedDataFor: TWO_MINUTES,
      transformResponse: (response) => response.data,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.EMPRESAS]
    }),
    empresaById: builder.query({
      query: (id) => `/empresas/${id}`,
      keepUnusedDataFor: 10,
      providesTags: [PROVIDES_AND_INVALIDATE_TAGS.EMPRESAS]
    }),
    // mutations
    saveEmpresa: builder.mutation({
      query: (body) => ({
        url: '/empresas',
        method: 'POST',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.EMPRESAS]
    }),
    updateEmpresa: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/empresas/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.EMPRESAS]
    }),
    deleteEmpresa: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/empresas/status/${id}`,
        body: { activo },
        method: 'PATCH'
      }),
      invalidatesTags: [PROVIDES_AND_INVALIDATE_TAGS.EMPRESAS]
    }),

    // ? actividades economicas
    actividadesEconomicas: builder.query({
      query: (filter = '') => {
        const params = filter.length ? { search: filter } : {}
        return {
          url: '/generales/actividad-economica',
          params: {
            limit: 100,
            ...params
          }
        }
      },
      transformResponse: (response) => response.data,
      keepUnusedDataFor: TWO_MINUTES
    }),
    // ? perfil
    getProfileById: builder.query({
      query: (id) => `/user/${id}`,
      keepUnusedDataFor: 10
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
  useDeleteIncapcityMutation,
  useUpdateIncapacityMutation,
  useSaveIncapacityMutation,
  useSaveExpedientMutation,
  useSaveInterrogationMutation,
  useSavePerinatalDataMutation,
  useSavePathologicalDiseasesMutation,
  useSaveObstetricMutation,
  useSaveNoPathologicalDiseasesMutation,
  useSaveHereditaryMutation,
  useSaveGinecologicalMutation,
  useSaveFisicalExplorationMutation,
  useSaveAllergiesMutation,
  useLazyGetUsersQuery,
  useLazyGetSymptomsOfAllergiesQuery,
  useLazyGetRolesQuery,
  useLazyGetRelationShipToDiseasesQuery,
  useLazyGetRelationshipsQuery,
  useLazyGetProfessionsQuery,
  useLazyGetPeoplesQuery,
  useLazyGetPathologicalDiseasesQuery,
  useLazyGetModulesQuery,
  useLazyGetIdentityCardQuery,
  useLazyGetHereditaryDiseasesQuery,
  useLazyGetAllergicsQuery,
  useGetUsersQuery,
  useGetSymptomsOfAllergiesQuery,
  useGetSmookingDataQuery,
  useGetSexuallyTransmittedDiseasesQuery,
  useGetRelationShipToDiseasesQuery,
  useGetRelationshipsQuery,
  useGetProfessionsQuery,
  useGetPregnancyHistoryQuery,
  useGetPregnancyHistoryForFileQuery,
  useGetPerinatalDataQuery,
  useGetPeoplesQuery,
  useGetPathologicalDiseasesQuery,
  useGetPathologicalDiseasesForFileQuery,
  useGetObstetricDiseasesQuery,
  useGetObstetricDiseasesForFileQuery,
  useGetNoPathologicalDiseaseForFileQuery,
  useGetModulesQuery,
  useGetIncapacitiesQuery,
  useGetIdentityCardQuery,
  useGetHereditaryDiseasesQuery,
  useGetHereditariesForFileQuery,
  useGetGinecologicalForFileQuery,
  useGetForContraceptiveMethodsQuery,
  useGetFisicalExplorationForFileQuery,
  useGetEtsQuery,
  useGetDrugsQuery,
  useGetContraceptiveMethodsQuery,
  useGetComplicationsQuery,
  useGetClinicalRecordsQuery,
  useGetAllergiesForFileQuery,
  useGetAllergicsQuery,
  useGetAlcholismDataQuery,
  useGetInterrogationForFileQuery,
  useGetFolderByIdQuery,
  useLazyGetFolderByIdQuery,
  useGetFolderPeoplesQuery,
  useSeveritiesQuery,
  useGetDiagnosticsQuery,

  useDiagnosticosQuery,
  useCreateDiagnosticoMutation,
  useConsultasQuery,
  useConsultaDiagnosticosQuery,
  usePrescripcionByIdQuery,
  useUpdateDiagnosticoMutation,
  useDeleteDiagnosticoMutation,

  useLoginMutation,
  useConsultaByIdQuery,
  useLazyConsultaByIdQuery,
  useConsultaDiagnosticoQuery,
  useConsultasPorExpedienteQuery,
  useUpdateConsultaMutation,
  useDeleteConsultaMutation,
  // generales
  useGeneroQuery,
  useLazyGeneroQuery,
  useDepartamentosQuery,
  useLazyDepartamentosQuery,
  usePaisesQuery,
  useLazyPaisesQuery,
  useLazyMunicipioQuery,
  useMunicipioQuery,

  //? MEDICOS
  useMedicosQuery,
  useDoctorByIdQuery,
  useLazyDoctorByIdQuery,
  useCrearMedicoMutation,
  useUpdateMedicoMutation,
  useDeleteMedicoMutation,
  useEspecialidadesQuery,
  useSubEspecialidadesQuery,
  useUpdateMedicoByConsultaMutation,
  useLazyMedicosQuery,
  useSearchConsultaByIdQuery,
  useSendReportMutation,
  useChangeDoctorMutation,

  // ? AGENDA DE CITAS
  useDatesQuery,
  useStatusDatesQuery,
  useNewDateMutation,
  useUpdateDateMutation,

  //? especialidades
  useCrearEspecialidadMutation,
  useUpdateEspecialidadMutation,
  useDeleteEspecialidadMutation,

  //? sub especialidades
  useCrearSubEspecialidadMutation,
  useUpdateSubEspecialidadMutation,
  useDeleteSubEspecialidadMutation,

  // ? sucursales
  useGetSucursalesQuery,
  useDeleteLocaleMutation,
  // ? enfermedades hereditarias
  useHereditariesQuery,
  useSaveHereditaryDiseaseMutation,
  useUpdateHereditaryDiseaseMutation,
  useDeleteHereditaryDiseaseMutation,
  useLazyHereditaryByIdQuery,

  // ? alergias
  useCreateAllergicsMutation,
  useUpdateAllergicsMutation,
  useAllergicByIdQuery,
  useDeleteAllergicsMutation,

  // ? complicaciones
  useAddComplicationMutation,
  useUpdateComplicationMutation,
  useDeleteComplicationMutation,
  useGetComplicationByIdQuery,

  // ? menstruacion
  useAddMenstruationMutation,
  useUpdateMenstruationMutation,
  useDeleteMenstruationMutation,
  useGetMenstruacionByIdQuery,

  // ? ets
  useAddEtsMutation,
  useUpdateEtsMutation,
  useDeleteEtsMutation,
  useGetEtsByIdQuery,
  useGetEnfermedadesTransmicionSexualQuery,

  // ? anticoncepcion
  useAddAnticoncepcionMutation,
  useUpdateAnticoncepcionMutation,
  useDeleteAnticoncepcionMutation,
  useGetAnticoncepcionByIdQuery,

  // ? sintomas de alergias
  useAddAllergicSymptomMutation,
  useUpdateAllergicSymptomMutation,
  useDeleteAllergicSymptomMutation,
  useGetAllergicSymptomsByIdQuery,

  // ? patologias
  useAddPathologicalDiseaseMutation,
  useUpdatePathologicalDiseaseMutation,
  useDeletePathologicalDiseaseMutation,
  useFindPathologicalDiseaseByIdQuery,

  // ? ENFERMEDADES PATOLOGICAS
  useAddPathologicalDiseaseFeatureMutation,
  useUpdatePathologicalDiseaseFeatureMutation,
  useDeletePathologicalDiseaseFeatureMutation,
  useFindPathologicalDiseaseFeatureByIdQuery,
  useGetPathologicalDiseasesFeaturesQuery,

  // ? roles
  useGetRolesQuery,
  useRoleByIdQuery,
  usePermissionsByRoleIdQuery,
  // ? Asignar permisos a roles
  useAsignPermissionsToRoleMutation,
  useAsignPermissionUpdateMutation,

  // ? empresas
  useEmpresasQuery,
  useEmpresaByIdQuery,
  useLazyEmpresaByIdQuery,
  useSaveEmpresaMutation,
  useUpdateEmpresaMutation,
  useDeleteEmpresaMutation,

  // ? actividades economicas
  useActividadesEconomicasQuery,
  // ? profile
  useGetProfileByIdQuery,

  // ?anticonceptivos get
  useAnticonceptivosQuery,

  useGetAlteracionMenstrualQuery
} = clinicalApi
